import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from '@dr.pogodin/react-helmet'
import Toast from '../../utils/toast'
import { useAuth } from '../../hooks/useAuth'
import { isExecutive, firstAllowedRoute } from '../../utils/permissions'
import logo from '../../assets/main/logo.d99exch.png'
import { useSiteBrand } from '../../siteBrand'
import './style.css'
import { DESKTOP_VIEWPORT } from '../../hooks/useViewport'

function Login() {
  const site = useSiteBrand() // Owner Panel logo (siteBrand.js); built-in when not uploaded
  const navigate = useNavigate()
  const { loginAsync, isLoggingIn, isAuthenticated, user } = useAuth()

  // Clear any stale password change flag when landing on login page (not authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.removeItem('needPasswordChange')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  })

  const [errors, setErrors] = useState({
    username: '',
    password: '',
    general: ''
  })


  // Redirect after login
  useEffect(() => {
    if (!isAuthenticated) return

    // sessionStorage flag is set immediately in useAuth onSuccess,
    // so we can check it right away without waiting for profile
    const needChange = sessionStorage.getItem('needPasswordChange') === 'true'
    if (needChange) {
      navigate('/reset-password', { replace: true })
      return
    }

    // For normal login, wait until profile (user) is loaded. Executives land
    // on the first page their permission map allows (never a denied route).
    if (user) {
      navigate(isExecutive() ? firstAllowedRoute() : '/admin/market-analysis', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  const validateField = (name, value) => {
    let error = ''

    if (name === 'username') {
      if (!value || value.trim() === '') {
        error = 'The username field is required'
      }
      // Accept either email format or username format (no validation needed for username)
    } else if (name === 'password') {
      if (!value || value.trim() === '') {
        error = 'The password field is required'
      }
    }

    return error
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData({
      ...formData,
      [name]: value
    })

    // Real-time validation
    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    const error = validateField(name, value)
    setErrors({
      ...errors,
      [name]: error
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isLoggingIn) return

    // Validate all fields
    const usernameError = validateField('username', formData.username)
    const passwordError = validateField('password', formData.password)

    setErrors({
      username: usernameError,
      password: passwordError,
      general: ''
    })

    // If no errors, proceed with login
    if (!usernameError && !passwordError) {
      try {
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)

        const credentials = {
          password: formData.password
        }

        if (isEmail) {
          credentials.email = formData.username
        } else {
          credentials.username = formData.username
        }

        const response = await loginAsync(credentials)

        if (response?.require2FA) {
          // Navigate to 2FA page
          navigate('/loginauth/2', {
            state: {
              staffId: response.staffId,
              ownerId: response.ownerId,
              // 'telegram' pushed a code; 'android' / 'google' mean it is
              // already on the operator's phone, so neither the "sent to
              // Telegram" toast nor Resend applies.
              method: response.method
            }
          });
          Toast.fire({
            icon: 'info',
            title: response.method === 'android'
              ? 'Please enter the code from your Secure Auth Verification App'
              : response.method === 'google'
                ? 'Please enter the code from your authenticator app'
                : 'Please enter 2FA Code sent to Telegram'
          });
          return;
        }

        if (response?.success) {
          if (response?.first_login === false) {
            Toast.fire({
              icon: 'success',
              title: 'Welcome, You are logged in first time. Please change your password to continue.'
            });
          } else {
            Toast.fire({
              icon: 'success',
              title: 'Success'
            });
          }
          // useEffect will handle redirect after profile loads
        } else {
          const raw = response?.message || response?.error
          const msg = /not found/i.test(raw || '') ? 'UserName not available' : (raw || 'Login failed. Please try again.')
          // Same as the reference (resetUserValue): a failed login clears the form
          setFormData({ username: '', password: '' })
          setErrors({
            username: '',
            password: '',
            general: msg
          })
          Toast.fire({
            icon: 'error',
            title: msg
          });
        }
      } catch (error) {
        const raw = error?.response?.data?.message || error?.response?.data?.error || error?.message
        const msg = /not found/i.test(raw || '') ? 'UserName not available' : (raw || 'Login failed. Please check your credentials.')
        setFormData({ username: '', password: '' })
        setErrors({
          username: '',
          password: '',
          general: msg
        })
        Toast.fire({
          icon: 'error',
          title: msg
        });
      }
    }
  }

  return (
    <>
      <Helmet>
        <meta name="viewport" content={DESKTOP_VIEWPORT} />
      </Helmet>
      <section className="login-mn">
        <div className="log-logo m-b-18">
          <img src={site.logo || logo} alt="DIAMOND EXCH 99" style={{ maxWidth: '250px', maxHeight: '100px' }} />
        </div>
        <div className="log-fld">
          <h2 className="text-center">Sign In</h2>

          <form
            autoComplete="off"
            data-vv-scope="form-login"
            action=""
            method="POST"
            className="form-horizontal"
            onSubmit={handleSubmit}
          >

            <div id="input-group-1" role="group" className="form-group">
              <div>
                <input
                  id="input-1"
                  name="username"
                  type="text"
                  placeholder="Username"
                  className="form-control"
                  autoComplete="username"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoggingIn}
                />
                {errors.username && <span className="error">{errors.username}</span>}
              </div>
            </div>
            <div id="input-group-2" role="group" className="form-group">
              <div>
                <input
                  id="input-2"
                  name="password"
                  type="password"
                  placeholder="password"
                  className="form-control"
                  autoComplete="new-password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoggingIn}
                />
                {errors.password && <span className="error">{errors.password}</span>}
              </div>
            </div>
            <div className="form-group text-center">
              <button
                type="submit"
                className="btn btn-submit btn-login btn-primary"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Logging in...' : 'Login'} <i className="fas fa-sign-in-alt"></i>
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  )
}

export default Login

